import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { Map as MapIcon, Maximize2, Minimize2, Table2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { Alert, Button, SegmentedControl, cn } from '@/components/ui';
import { PointPairsTab } from '@/components/inputs/PointPairsTab';
import { PointPairDetails } from '@/components/inputs/PointPairDetails';
import { InteractiveMap } from '@/components/map/InteractiveMap';
import type { useAnalysis } from '@/hooks/useAnalysis';
import { useAppStore, selectActivePointCount } from '@/store/appStore';
import { useProgressStore, selectIsProcessing } from '@/store/progressStore';
import { useBlunderVerdict } from '@/store/selectors';
import { DetectionReview } from './DetectionReview';
import { SolutionSummary } from './SolutionSummary';
import { TrialReview } from './TrialReview';
import { VerdictPanel } from './VerdictPanel';
import { getNextAction, unitsLabel } from './adjustmentDisplay';

type Analysis = ReturnType<typeof useAnalysis>;

const store = useAppStore.getState;

/** Summary and verdict: the fit's state and the next decision. */
const SummarySection = ({ analysis }: { analysis: Analysis }) => {
  const busy = useProgressStore(selectIsProcessing);
  const {
    solution,
    solutionStale,
    pointPairs,
    units,
    customScale,
    canUndoApply,
    trialActive,
    alpha,
  } = useAppStore(
    useShallow((s) => ({
      solution: s.solution,
      solutionStale: s.solutionStale,
      pointPairs: s.pointPairs,
      units: s.units,
      customScale: s.customScale,
      canUndoApply: s.lastApplySnapshot !== null,
      trialActive: s.trialPreview !== null,
      alpha: s.dataSnoopingResults?.alpha ?? s.selectedAlpha,
    }))
  );
  const { verdict, flaggedPointIds, detectionStale } = useBlunderVerdict();
  const activePointCount = selectActivePointCount({ pointPairs });
  const hasSolution = !!solution && !solution.error;
  const canCheck = activePointCount >= 3;
  const nextAction = getNextAction({
    hasSolution,
    solutionStale,
    canCheck,
    detectionCurrent:
      !detectionStale &&
      verdict.combinatorial !== null &&
      verdict.dataSnooping !== null,
  });
  const flagsPending =
    !detectionStale && flaggedPointIds.length > 0 && nextAction === 'export';
  const previewEmphasis = flagsPending && !trialActive;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(19rem,25rem)]">
      <SolutionSummary
        solution={solution}
        solutionStale={solutionStale}
        pointPairs={pointPairs}
        activePointCount={activePointCount}
        units={units}
        customScale={customScale}
        canUndoApply={canUndoApply}
        nextAction={nextAction}
        detectionStale={detectionStale}
        deferPrimary={trialActive || flagsPending}
        busy={busy}
        onSolve={() => analysis.handleSolve()}
        onCheckBlunders={analysis.handleCheckForBlunders}
        onContinueToExport={() => store().setActiveTab('transform')}
        onUndoApply={() => store().undoLastApply()}
        onHoldScale={(scale) => analysis.handleSolve(scale)}
        onEstimateScale={() => analysis.handleSolve('')}
        onSelectPoint={(id) => store().setSelectedPointId(id)}
      />
      <VerdictPanel
        verdict={verdict}
        detectionStale={detectionStale}
        hasSolution={hasSolution}
        canCheck={canCheck}
        alpha={alpha}
        pointPairs={pointPairs}
        emphasizePreview={previewEmphasis}
        showRecheck={nextAction !== 'check'}
        busy={busy}
        onRecheck={analysis.handleCheckForBlunders}
        onPreview={analysis.handlePreviewRejections}
        onSelectPoint={(id) => store().setSelectedPointId(id)}
      />
    </div>
  );
};

const TrialSection = ({ analysis }: { analysis: Analysis }) => {
  const { trial, solution, solutionStale } = useAppStore(
    useShallow((s) => ({
      trial: s.trialPreview,
      solution: s.solution,
      solutionStale: s.solutionStale,
    }))
  );
  if (!trial) return null;
  return (
    <TrialReview
      trial={trial}
      solution={solution}
      solutionStale={solutionStale}
      onApply={() => analysis.handleSolve(undefined, trial.rejectedIds)}
      onDismiss={() => store().clearTrialPreview()}
      onSelectPoint={(id) => store().setSelectedPointId(id)}
    />
  );
};

const PairsSection = ({
  onEditPair,
  onImportPairs,
}: {
  onEditPair: (id: number) => void;
  onImportPairs: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const state = useAppStore(
    useShallow((s) => ({
      pointPairs: s.pointPairs,
      system1Points: s.system1Points,
      system2Points: s.system2Points,
      matchingMode: s.matchingMode,
      detectedOffset: s.detectedOffset,
      solution: s.solution,
      solutionStale: s.solutionStale,
      trialSolution: s.trialPreview?.solution,
      selectedPointId: s.selectedPointId,
    }))
  );
  const { flaggedPointIds } = useBlunderVerdict();
  return (
    <PointPairsTab
      {...state}
      flaggedPointIds={flaggedPointIds}
      onSelectPoint={(id) => store().setSelectedPointId(id)}
      onEditPair={onEditPair}
      onAddPair={() => store().addPointPair()}
      onSetWeight={(id, weight) => store().setPointWeight(id, weight)}
      onResetWeights={() => store().resetAllWeights()}
      onImportPairs={onImportPairs}
      onAcceptPairs={(pairs, mode, offset) =>
        store().acceptMatchedPairs(pairs, mode, offset ?? null)
      }
      onMatchingModeChange={(mode) => store().setMatchingMode(mode)}
    />
  );
};

const DetectionSection = ({ analysis }: { analysis: Analysis }) => {
  const busy = useProgressStore(selectIsProcessing);
  const state = useAppStore(
    useShallow((s) => ({
      blunderResults: s.blunderResults,
      dataSnoopingResults: s.dataSnoopingResults,
      combinatorialStale: s.combinatorialStale,
      dataSnoopingStale: s.dataSnoopingStale,
      activeDetectionMethod: s.activeDetectionMethod,
      pointPairs: s.pointPairs,
      solution: s.solutionStale ? null : s.solution,
      rejectionLevel: s.rejectionLevel,
      runAllMaxLevel: s.runAllMaxLevel,
      viewAllSolutions: s.viewAllSolutions,
      selectedAlpha: s.selectedAlpha,
    }))
  );
  // Nothing to detect until pairs exist.
  if (state.pointPairs.length === 0) return null;
  return (
    <DetectionReview
      {...state}
      busy={busy}
      onRejectionLevelChange={(level) => store().setRejectionLevel(level)}
      onRunAllMaxLevelChange={(level) => store().setRunAllMaxLevel(level)}
      onViewAllChange={(viewAll) => store().setViewAllSolutions(viewAll)}
      onSelectedAlphaChange={(alpha) => store().setSelectedAlpha(alpha)}
      onRunBlunder={analysis.handleRunBlunder}
      onRunAllBlunder={analysis.handleRunAllBlunder}
      onRunDataSnooping={analysis.handleRunDataSnooping}
      onPreviewRejections={analysis.handlePreviewRejections}
    />
  );
};

const MapSection = ({
  wide,
  onEditPair,
}: {
  wide: boolean;
  onEditPair: (id: number) => void;
}) => {
  const {
    units,
    pointPairs,
    system1Points,
    system2Points,
    solution,
    solutionStale,
    selectedPointId,
    currentFit,
    previousFit,
    trial,
  } = useAppStore(
    useShallow((s) => ({
      units: s.units,
      pointPairs: s.pointPairs,
      system1Points: s.system1Points,
      system2Points: s.system2Points,
      solution: s.solution,
      solutionStale: s.solutionStale,
      selectedPointId: s.selectedPointId,
      currentFit: s.currentFit,
      previousFit: s.previousFit,
      trial: s.trialPreview,
    }))
  );
  const { flaggedPointIds } = useBlunderVerdict();
  // Retain the input geometry of the displayed fit while edits await a solve.
  // Weight symbols still reflect the user's current decisions.
  const mapPairs = useMemo(() => {
    if (trial) return trial.pointPairs;
    if (!solutionStale || !currentFit) return pointPairs;
    const fittedPairs = new Map(
      currentFit.pointPairs.map((pair) => [pair.id, pair])
    );
    return pointPairs.map((pair) => {
      const fitted = fittedPairs.get(pair.id);
      return fitted ? { ...fitted, weight: pair.weight } : pair;
    });
  }, [trial, solutionStale, currentFit, pointPairs]);
  const geometryUnavailable = solutionStale && !currentFit && !trial;

  return (
    <>
      {geometryUnavailable && (
        <Alert tone="warning" className="mb-3">
          Previous fit geometry is unavailable after reload. Re-solve to display
          the fit.
        </Alert>
      )}
      <InteractiveMap
        defaultExpanded
        compact={!wide}
        units={units}
        pointPairs={mapPairs}
        system1Points={system1Points}
        system2Points={system2Points}
        solution={geometryUnavailable ? null : solution}
        solutionStale={solutionStale}
        selectedPointId={selectedPointId}
        onSelectPoint={(id) => store().setSelectedPointId(id)}
        onSetWeight={(id, weight) => store().setPointWeight(id, weight)}
        onLinkPoints={(a, b) => store().linkPoints(a, b)}
        onRemovePair={(id) => store().unlinkPair(id)}
        onEditPair={onEditPair}
        previousFit={trial ? currentFit : previousFit}
        trialSolution={trial?.solution}
        flaggedPointIds={flaggedPointIds}
      />
    </>
  );
};

const PairDetailsSection = ({
  pairId,
  onClose,
}: {
  pairId: number | null;
  onClose: () => void;
}) => {
  const {
    pair,
    system1Points,
    system2Points,
    solution,
    trialSolution,
    solutionStale,
    units,
  } = useAppStore(
    useShallow((s) => ({
      pair: s.pointPairs.find((candidate) => candidate.id === pairId),
      system1Points: s.system1Points,
      system2Points: s.system2Points,
      solution: s.solution,
      trialSolution: s.trialPreview?.solution,
      solutionStale: s.solutionStale,
      units: s.units,
    }))
  );
  if (!pair) return null;
  return (
    <PointPairDetails
      pair={pair}
      system1Points={system1Points}
      system2Points={system2Points}
      onUpdatePair={(...args) => store().updatePointPair(...args)}
      onRemovePair={(id) => store().unlinkPair(id)}
      onClose={onClose}
      solution={solution}
      trialSolution={trialSolution}
      solutionStale={solutionStale}
      units={unitsLabel(units)}
    />
  );
};

/**
 * Adjust: solution summary and verdict on top; the point table, trial, and
 * detection review beside a sticky map on wide screens. Phones switch
 * between the table and the map.
 */
export const AdjustmentWorkspace = ({
  analysis,
  onImportPairs,
}: {
  analysis: Analysis;
  onImportPairs: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const [editingPairId, setEditingPairId] = useState<number | null>(null);
  const [mapWide, setMapWide] = useState(false);
  const [view, setView] = useState<'table' | 'map'>('table');
  const editPair = useCallback((id: number) => {
    store().setSelectedPointId(id);
    setEditingPairId(id);
  }, []);

  return (
    <div className="space-y-4">
      <SummarySection analysis={analysis} />
      <SegmentedControl
        aria-label="Workspace view"
        fullWidth
        className="xl:hidden"
        value={view}
        onValueChange={setView}
        options={[
          { value: 'table', label: 'Table', icon: <Table2 /> },
          { value: 'map', label: 'Map', icon: <MapIcon /> },
        ]}
      />
      <div
        className={cn(
          'grid items-start gap-4',
          mapWide
            ? 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'
            : 'xl:grid-cols-[minmax(0,1fr)_minmax(24rem,36%)]'
        )}
      >
        <div
          className={cn('min-w-0 space-y-4', view === 'map' && 'max-xl:hidden')}
        >
          <TrialSection analysis={analysis} />
          <PairsSection onEditPair={editPair} onImportPairs={onImportPairs} />
          <DetectionSection analysis={analysis} />
        </div>
        <aside
          aria-label="Adjustment map"
          className={cn(
            'min-w-0 xl:sticky xl:top-[4.5rem] xl:max-h-[calc(100vh-5.5rem)] xl:overflow-y-auto',
            view === 'table' && 'max-xl:hidden'
          )}
        >
          <div className="mb-2 hidden justify-end xl:flex">
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={mapWide}
              leadingIcon={mapWide ? <Minimize2 /> : <Maximize2 />}
              onClick={() => setMapWide((wide) => !wide)}
            >
              {mapWide ? 'Narrow map' : 'Widen map'}
            </Button>
          </div>
          <MapSection wide={mapWide} onEditPair={editPair} />
        </aside>
      </div>
      <PairDetailsSection
        pairId={editingPairId}
        onClose={() => setEditingPairId(null)}
      />
    </div>
  );
};
